import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Grid,
  Divider,
  Card,
  CardContent,
  Alert,
  Backdrop,
  useTheme,
  useMediaQuery,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import DocumentsTable from 'components/common/DocumentsTable';
import TransferListComponent from 'components/common/TransferListComponent';
import { CloudStorageObject } from 'interfaces/CloudStorageObject';
import ErrorPage from 'pages/ErrorPage/ErrorPage';
import { ChangeEvent, useCallback, useContext, useEffect, useReducer, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router';
import UserContext from '../../Context/UserProvider';
import QuestionTable from "../../components/charging/SiteAccessPolicyComponent/QuestionTable";
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import PolicyIcon from '@mui/icons-material/Policy';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InstructionsIcon from '@mui/icons-material/Assignment';
import DashboardCard from 'components/dashboard/common/DashboardCard';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import ClearIcon from '@mui/icons-material/Clear';
import { useApiService } from '../../services/ApiService/ApiService';

type SignedUrlResponse = {
    ramsDocuments: CloudStorageObject[];
};

type Question = {
    id: string;
    text: string;
    type: string;
}

interface Item {
    uuid: string;
    name: string;
}

// Better Location Selection Component
const LocationSelector = ({ left, right, setLeft, setRight }: { 
    left: Item[], 
    right: Item[], 
    setLeft: React.Dispatch<React.SetStateAction<Item[]>>, 
    setRight: React.Dispatch<React.SetStateAction<Item[]>> 
}) => {
    const handleSelectAll = () => {
        setRight([...right, ...left]);
        setLeft([]);
        toast.success(`All ${left.length} locations selected`);
    };

    const handleClearAll = () => {
        setLeft([...left, ...right]);
        setRight([]);
        toast.success('All locations cleared');
    };

    const handleToggleLocation = (item: Item) => {
        if (right.some(selected => selected.uuid === item.uuid)) {
            // Remove from selected
            setRight(right.filter(selected => selected.uuid !== item.uuid));
            setLeft([...left, item]);
        } else {
            // Add to selected
            setLeft(left.filter(available => available.uuid !== item.uuid));
            setRight([...right, item]);
        }
    };

    const handleRemoveLocation = (item: Item) => {
        setRight(right.filter(selected => selected.uuid !== item.uuid));
        setLeft([...left, item]);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Controls */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1 }}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SelectAllIcon />}
                    onClick={handleSelectAll}
                    disabled={left.length === 0}
                    sx={{ fontSize: '0.75rem', px: 1.5, py: 0.5 }}
                >
                    Select All
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ClearIcon />}
                    onClick={handleClearAll}
                    disabled={right.length === 0}
                    sx={{ fontSize: '0.75rem', px: 1.5, py: 0.5 }}
                >
                    Clear All
                </Button>
            </Box>

            {/* Selected Locations Summary */}
            {right.length > 0 && (
                <Box>
                    <Typography variant="subtitle2" gutterBottom>
                        Selected Locations ({right.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {right.map((item) => (
                            <Chip
                                key={item.uuid}
                                label={item.name}
                                onDelete={() => handleRemoveLocation(item)}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Available Locations */}
            <Box>
                <Typography variant="subtitle2" gutterBottom>
                    Available Locations ({left.length})
                </Typography>

                <Paper 
                    sx={{ 
                        maxHeight: 300, 
                        overflow: 'auto',
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <List dense>
                        {left.map((item) => (
                            <ListItem key={item.uuid} disablePadding>
                                <ListItemButton 
                                    onClick={() => handleToggleLocation(item)}
                                    sx={{ py: 1 }}
                                >
                                    <ListItemText 
                                        primary={item.name}
                                        primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                        {left.length === 0 && (
                            <ListItem>
                                <ListItemText 
                                    primary="No available locations"
                                    primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        color: 'text.secondary',
                                        textAlign: 'center'
                                    }}
                                />
                            </ListItem>
                        )}
                    </List>
                </Paper>
            </Box>
        </Box>
    );
};

function SiteAccessPolicyEditPage(props: any) {

    const { userInfo } = useContext(UserContext)
    const { fetchWithAuth } = useApiService();
    const [questions, setQuestions] = useState<Question[]>([]);
    const [ramsDocuments, setRamsDocuments] = useState<CloudStorageObject[]>([]);
    const [missingInput, setMissingInput] = useState("")
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const location = useLocation() as any;
    const [siteAccessPolicyUuid, setSiteAccessPolicyUuid] = useState(location.state?.siteAccessPolicyUuid);

    const [left, setLeft] = useState<Item[]>([]); // Explicit type
    const [right, setRight] = useState<Item[]>([]); // Explicit type

    const navigate = useNavigate();

    useEffect(() => {
        if (location.state?.siteAccessPolicyUuid) {
            setSiteAccessPolicyUuid(location.state.siteAccessPolicyUuid);
        }
    }, [location.state?.siteAccessPolicyUuid]);

    const initialState = {
        surveyName: "",
    }

    const [isError, setIsError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [videoFiles, setVideoFiles] = useState<CloudStorageObject[]>([]);
    const [scopeValue, setScopeValue] = useState({}) as any;
    const [surveyName, setSurveyName] = useState("");
    const [showAddInstructionDialog, setShowAddInstructionDialog] = useState(false);
    const [newInstructionText, setNewInstructionText] = useState("");
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: boolean }>({});

    useEffect(() => {
        // Initialize document and video files separately from ramsDocuments
        // setDocumentFiles(ramsDocuments?.filter(doc => doc.type === 'DOCUMENT'));
        setVideoFiles(ramsDocuments);
    }, [ramsDocuments]);

    const handleVideoChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            const newFiles: CloudStorageObject[] = Array.from(event.target.files).map((file, index) => ({
                id: `temp-${Date.now()}-${index}`, // Temporary unique ID
                name: file.name,
                status: 'Not Saved' as 'Not Saved', // Custom status to indicate the file is not yet saved
                file: file, // Store the file object for uploading
                putSignedUrl: "",
                deleteSignedUrl: "",
                fullName: ""
            }));

            // Add new files to the start of the array
            // setRamsDocuments(prev => [...newFiles, ...prev]);


            setRamsDocuments(prev => {
                return [...newFiles, ...prev];
            });
        }
    };

    const removeDocument = (id: string) => {
        setVideoFiles(prev => prev.filter(doc => doc.id !== id));
    };

    const save = async () => {
        setIsUploading(true); // Indicate the start of the upload process

        const validationStatus = validateForm()
        if (!validationStatus) {
            setIsUploading(false);
            return null; // Return null if validation fails
        }

        // Merge documentFiles and videoFiles to create a new snapshot of ramsDocuments
        const updatedRamsDocuments = [...checking_null_undefined(videoFiles)];

        try {
            // Handle deletion of documents marked as 'Deleted'
            const toDelete = updatedRamsDocuments.filter(doc => doc.status === 'Deleted');
            const deletePromises = toDelete.map(doc =>
                fetch(doc.deleteSignedUrl, {
                    method: 'DELETE',
                })
            );
            await Promise.all(deletePromises);

            // Remove deleted documents from the updatedRamsDocuments before upload operations
            let postDeleteDocuments = updatedRamsDocuments.filter(doc => doc.status !== 'Deleted');

            // Call submitSurvey here to ensure it's always executed
            const surveyResult = await submitSurvey();

            console.log("surveyResult " + surveyResult);

            // Filter out 'Not Saved' documents that have a file to upload
            const toUpload = postDeleteDocuments.filter(doc => doc.status === 'Not Saved' && doc.file);

            if (toUpload.length > 0) {
                const fileInfos = toUpload.map(doc => ({ name: doc.name}));

                const urls = await fetchSignedUrls(fileInfos, surveyResult);

                const uploadPromises = urls.map((url, index) => {
                    const file = toUpload[index].file!; // Non-null assertion is safe due to the filter
                    return fetch(url.putSignedUrl, {
                        method: 'PUT',
                        body: file,
                        headers: {
                            'Content-Type': file.type,
                        },
                    });
                });

                await Promise.all(uploadPromises);

                // Update the status of uploaded documents to 'Saved'
                postDeleteDocuments = postDeleteDocuments.map(doc => ({
                    ...doc,
                    status: doc.status === 'Not Saved' && doc.file ? 'Saved' : doc.status
                }));
            }

            // Set the final state of ramsDocuments
            setRamsDocuments(postDeleteDocuments);

            toast.success('Site Access Policy saved.', {
                position: 'bottom-center',
                duration: 5000,
            });

            navigate('/share/business/site-access-policies');
        } catch (error) {
            setErrorMessage("Error saving Site Access Policy");
            console.error('Error uploading files:', error);
        }

        setIsUploading(false); // Indicate the end of the upload process

        // Scroll to the top of the page
        window.scrollTo({ top: 0, behavior: "smooth" });

    };

    useEffect(() => {
        if (errorMessage) {
            toast.error(errorMessage, {
                position: 'bottom-center',
                duration: 5000,
            });
        }
        setErrorMessage(null);
    }, [errorMessage]);


    // Update the type for files parameter to match the expected structure
    const fetchSignedUrls = async (fileInfos: { name: string; }[], sasUuid: string): Promise<CloudStorageObject[]> => {
        try {
            const response = await fetchWithAuth('/videos/signedurls/get', {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ 
                    files: fileInfos, 
                    businessUuid: userInfo.businessUuid, 
                    siteAccessPolicyUuid: sasUuid 
                }),
            });

            if (!response.ok) {
                setIsError(true);
                throw new Error('Failed to fetch signed URLs');
            }

            const data: SignedUrlResponse = await response.json();
            return data.ramsDocuments;
        } catch (error) {
            console.error('Error fetching signed URLs:', error);
            setIsError(true);
            throw error;
        }
    };

    const updateDocuments = (updatedDocs: CloudStorageObject[]) => {
        setVideoFiles(updatedDocs);
    };

    const addNewQuestion = () => {
        setShowAddInstructionDialog(true);
    };

    const handleAddInstruction = () => {
        if (newInstructionText.trim()) {
            setQuestions((prevQuestions: Question[]) => [
                ...(Array.isArray(prevQuestions) ? prevQuestions : []),
                { 
                    id: `${Date.now()}-${Math.random()}`, 
                    text: newInstructionText.trim(), 
                    type: 'STATEMENT' 
                }
            ]);
            setNewInstructionText("");
            setShowAddInstructionDialog(false);
            
            // Clear instruction error since we now have at least one instruction
            console.log('Clearing instruction error after adding instruction');
            setFieldErrors(prev => {
                console.log('Previous field errors:', prev);
                const updated = {
                    ...prev,
                    instructions: false
                };
                console.log('Updated field errors:', updated);
                return updated;
            });
        }
    };

    const handleCancelAddInstruction = () => {
        setNewInstructionText("");
        setShowAddInstructionDialog(false);
    };

    function checking_null_undefined(array: any) {
        if (array === null || array === undefined) {
            return [];
        }
        return array;
    }

    const fetchSurvey = async () => {
        let bodyData: { [name: string]: string } = {};

        bodyData["uuid"] = userInfo.uuid;
        bodyData["businessUuid"] = userInfo.businessUuid;
        bodyData["siteAccessPolicyUuid"] = siteAccessPolicyUuid;
        
        try {
            const response = await fetchWithAuth('/site-access-policy/get', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bodyData)
            });

            if (response.status !== 200) {
                setIsError(true);
                return;
            }

            const res_json = await response.json();
            setLeft(checking_null_undefined(res_json.unSelectedDwellings));
            setRight(checking_null_undefined(res_json.selectedDwellings));
            setQuestions(res_json.survey?.siteInstructions?.map((e: any) => ({ ...e, id: e.uuid, text: e.text, type: e.type })));
            setRamsDocuments(
                res_json.survey?.videos
                    ? res_json.survey?.videos?.map((e: any) => ({
                        ...e,
                        id: e.fullName,
                        name: e.name,
                        type: e.type,
                        fullName: e.fullName,
                        signedUrl: e.signedUrl,
                    }))
                    : []
            );
            setSurveyName(res_json.survey?.name);
            const selectedScope = res_json.scope;

            if (selectedScope != null) {
                // Ensure the object structure is the same as in 'siteAccessPolicyOptions'
                const selectedValue = {
                    label: selectedScope, // assuming it has a 'name' field for the label
                    value: selectedScope, // the unique identifier, used as value
                    ...selectedScope, // spread the rest of the object properties if there are any others
                };
                setScopeValue(selectedValue);

            } else {
                const selectedValue = {
                    label: "", // assuming it has a 'name' field for the label
                    value: "", // the unique identifier, used as value
                };
                setScopeValue(selectedValue);
            }

            setLoading(false);
        } catch (error) {
            setIsError(true);
            console.error('Error fetching survey:', error);
        }
    };

    useEffect(() => {
        fetchSurvey();
    }, []);

    function validateForm() {
        const errors: string[] = [];
        const newFieldErrors: { [key: string]: boolean } = {};

        // Check for mandatory site access policy name
        if (!surveyName || surveyName.trim() === '') {
            errors.push('Site Access Policy Name is required');
            newFieldErrors.surveyName = true;
        }

        // Check for at least 1 instruction
        if (!questions || questions.length === 0) {
            errors.push('At least 1 instruction is required');
            newFieldErrors.instructions = true;
        }

        // Set field errors only if there are validation errors
        if (Object.keys(newFieldErrors).length > 0) {
            setFieldErrors(newFieldErrors);
        }

        // If there are errors, show them and return false
        if (errors.length > 0) {
            const errorMessage = errors.join('. ');
            toast.error(errorMessage, {
                position: 'bottom-center',
                duration: 5000,
            });
            return false;
        }

        // Clear field errors if validation passes
        setFieldErrors({});
        return true;
    }



    const handleQuestionUpdate = useCallback((updatedQuestions: any) => {
        console.log('handleQuestionUpdate called with:', updatedQuestions);
        setQuestions(updatedQuestions);
        
        // Only clear instruction error if we have at least one instruction
        // Don't automatically set errors - only during validation
        if (updatedQuestions && updatedQuestions.length > 0) {
            console.log('Clearing instruction error - questions exist');
            setFieldErrors(prev => {
                console.log('Previous field errors:', prev);
                const updated = {
                    ...prev,
                    instructions: false
                };
                console.log('Updated field errors:', updated);
                return updated;
            });
        }
        // Note: We don't automatically set errors when questions are empty
        // Errors are only set during validation (when save is clicked)
    }, []);

    const submitSurvey = async () => {
        try {
            const response = await fetchWithAuth('/site-access-policy/save', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    businessUuid: userInfo.businessUuid,
                    uuid: userInfo.uuid,
                    surveyName: surveyName,
                    questions: questions,
                    siteAccessPolicyUuid: siteAccessPolicyUuid,
                    ramsDocuments: ramsDocuments,
                    scope: scopeValue.value,
                    selectedDwellings: right,
                })
            });

            if (response.status === 505) {
                // Handle duplicate submission specifically
                toast.error('A site access policy with this name already exists. Please use a different name.', {
                    position: 'bottom-center',
                    duration: 5000,
                });
                return null;
            }

            if (!response.ok) {
                setIsError(true);
                return null; // Return null in case of HTTP errors
            }

            const res_json = await response.json();
            return res_json.uuid; // Assuming 'uuid' is the name of the key where the UUID is stored
        } catch (error) {
            console.error('Error submitting survey:', error);
            setIsError(true);
            return null;
        }
    };



    function onChange(e: any) {
        let action: any

        if (e.target) {
            action = {
                input: e.target.name,
                value: e.target.value,
            }

        } else {
            action = {
                input: e.name,
                value: e.value,
            }
        }
        if (action.input === "surveyName") {
            setSurveyName(action.value);
            // Clear field error when user starts typing
            setFieldErrors(prev => ({
                ...prev,
                surveyName: false
            }));
        }
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return <ErrorPage statusCode='505' />;
    }

    return (
        <>
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box sx={{ py: 3 }}>
                    {/* Site Access Policy Details Card */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} lg={4}>
                            <DashboardCard
                                title="Policy Details"
                                icon={<PolicyIcon />}
                                accentColor="primary"
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    <TextField
                                        fullWidth
                                        label="Site Access Policy Name"
                                        value={surveyName}
                                        name="surveyName"
                                        onChange={onChange}
                                        required
                                        variant="outlined"
                                        size="small"
                                        error={fieldErrors.surveyName}
                                        helperText={fieldErrors.surveyName ? 'Site Access Policy Name is required' : ''}
                                    />
                                </Box>
                            </DashboardCard>
                        </Grid>

                        {/* Locations Card */}
                        {scopeValue.value === 'DWELLING' && (
                            <Grid item xs={12} lg={8}>
                                <DashboardCard
                                    title="Locations"
                                    icon={<LocationOnIcon />}
                                    accentColor="secondary"
                                >
                                    <LocationSelector left={left} right={right} setLeft={setLeft} setRight={setRight} />
                                </DashboardCard>
                            </Grid>
                        )}
                    </Grid>

                    {/* Driver Instructions Card */}
                    <DashboardCard
                        title="Driver Instructions"
                        icon={<InstructionsIcon />}
                        accentColor="info"
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                                Configure instructions and questions for drivers accessing your charging locations. 
                            </Typography>
                            
                            <QuestionTable 
                                data={questions} 
                                onUpdate={handleQuestionUpdate}
                            />
                            
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={addNewQuestion}
                                sx={{ alignSelf: 'flex-start' }}
                            >
                                Add Instruction
                            </Button>
                            
                            {fieldErrors.instructions && (
                                <Alert severity="error">
                                    At least 1 instruction is required.
                                </Alert>
                            )}
                            
                            {missingInput && (
                                <Alert severity="error">
                                    {missingInput}
                                </Alert>
                            )}
                        </Box>
                    </DashboardCard>

                    {/* Save Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={save}
                            disabled={isUploading}
                            size="large"
                            sx={{ px: 4, py: 1.5 }}
                        >
                            Save Policy
                        </Button>
                    </Box>
                </Box>

                {/* Loading Overlay */}
                <Backdrop
                    sx={{ 
                        color: '#fff', 
                        zIndex: (theme) => theme.zIndex.drawer + 1 
                    }}
                    open={isUploading}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            </Container>

            {/* Add Instruction Dialog */}
            <Dialog
                open={showAddInstructionDialog}
                onClose={handleCancelAddInstruction}
                aria-labelledby="add-instruction-dialog-title"
                aria-describedby="add-instruction-dialog-description"
                maxWidth="md"
                fullWidth
            >
                <DialogTitle id="add-instruction-dialog-title">Add New Instruction</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="instruction-text"
                        label="Instruction Text"
                        type="text"
                        fullWidth
                        multiline
                        rows={4}
                        value={newInstructionText}
                        onChange={(e) => setNewInstructionText(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelAddInstruction}>Cancel</Button>
                    <Button onClick={handleAddInstruction} variant="contained">Add</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default SiteAccessPolicyEditPage;
